import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { Link } from 'gatsby';

import FooterLogo from '@images/logos/submittable-logo_no-box__white.svg';

import {
	FaFacebookF,
	FaInstagram,
	FaLinkedinIn,
	FaYoutube,
	FaPodcast,
	FaSpotify,
} from 'react-icons/fa';

// TODO Delete
// import PodcastApple from '@images/impact-audio/PodcastApple.svg';
// import PodcastSpotify from '@images/impact-audio/PodcastSpotify.svg';

import AccentLine from '@images/icons/AccentLine.svg';

const FooterContainer = styled.footer`
	background-color: ${({ theme }) => theme.colors.black.main};
	color: ${({ theme }) => theme.colors.white.main};
`;

const FooterWrapper = styled.div`
	max-width: 1320px;
	width: 90%;
	margin: 0 auto;
	padding: 48px 16px 64px;

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		padding: 100px 0 64px;
	}
`;

const NavigationGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 32px;

	${({ theme }) => theme.mediaQuery.breakpointMd} {
		grid-template-columns: repeat(2, 1fr);
	}

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		grid-template-columns: repeat(4, 1fr);
		gap: 48px;
	}
`;

const NavigationSection = styled.div``;

const SectionTitle = styled.h3`
	color: ${({ theme }) => theme.colors.white.mid};
	font-family: ${({ theme }) => theme.fonts.callout600};
	font-size: 1.3rem; /* 13px */
	line-height: 100%; /* 13px */
	letter-spacing: 0.13px;
	text-transform: uppercase;
`;

const LinkList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
`;

const LinkItem = styled.li``;

const FooterLink = styled(Link)`
	font-family: ${({ theme }) => theme.fonts.body};
	color: ${({ theme }) => theme.colors.white.main};
	text-decoration: none;
	font-size: 1.5rem;
	line-height: 36px; /* 240% */
	letter-spacing: 0.15px;
	transition: color 0.2s ease;

	&:hover {
		color: ${({ theme }) => theme.colors.white.dark};
	}

	&:focus {
		outline: 2px solid ${({ theme }) => theme.colors.primary.main};
		outline-offset: 2px;
	}
`;

const BadgeContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Badge = styled.span`
	background-color: ${({ theme }) => theme.colors.primary.main};
	color: ${({ theme }) => theme.colors.white.main};
	font-size: 1.4rem;
	font-weight: 500;
	padding: 4px 8px;
	border-radius: 4px;
`;

const Divider = styled.div`
	background-color: ${({ theme }) => theme.colors.white.mid};
	margin: 56px 0;
	height: 1px;
`;

const BottomSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 60px;

	@media (min-width: 640px) {
		gap: ${({ noMenu }) => (noMenu ? '40px' : '88px')};
	}
`;

const HeadlineContainer = styled.div`
	text-align: center;
`;

const MainHeadline = styled.p`
	font-family: ${({ theme }) => theme.fonts.heading};
	color: ${({ theme }) => theme.colors.white.main};
	font-size: 5rem;
	line-height: 110%; /* 105.6px */
	letter-spacing: -0.96px;
	text-wrap: balance;

	${({ theme }) => theme.mediaQuery.breakpointXl} {
		font-size: 14rem;
	}
`;

const HeadlineBlock = styled.span`
	display: block;
`;

const AccentedText = styled.span`
	position: relative;
	display: inline-block;

	span {
		position: relative;
		z-index: 2; /* text above */
	}

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		top: 0;
		height: 100%;
		background-image: url(${AccentLine});
		background-repeat: no-repeat;
		background-position: bottom center;
		background-size: contain; /* or set specific size */
		z-index: 1;
	}
`;

const SocialContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;

	@media (min-width: 640px) {
		flex-direction: row;
		justify-content: space-between;
	}
`;

const SocialIcons = styled.div`
	display: flex;
	gap: 16px;
`;

const SocialLink = styled.a`
	width: 24px;
	height: 24px;
	color: ${({ theme }) => theme.colors.white.main};
	transition: color 0.2s ease;

	&:hover {
		color: ${({ theme }) => theme.colors.white.dark};
	}

	&:focus {
		outline: 2px solid ${({ theme }) => theme.colors.primary.main};
		outline-offset: 2px;
	}
`;

const PodcastSection = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const PodcastLabel = styled.span`
	color: ${({ theme }) => theme.colors.white.main};
	font-family: ${({ theme }) => theme.fonts.body};
	font-size: 1.2rem;
	font-style: normal;
	font-weight: 500;
	line-height: 100%; /* 12px */
	letter-spacing: 0.12px;
	text-transform: uppercase;
`;

const FooterLogoImg = styled.img`
	margin: 0 auto calc(104px - 40px);
`;

const Footer = ({ footerRef, noMenu }) => {
	const socialIcons = [
		{
			icon: FaFacebookF,
			label: 'Facebook',
			href: 'https://www.facebook.com/submittable/',
		},
		{
			icon: FaInstagram,
			label: 'Instagram',
			href: 'https://www.instagram.com/submittable/',
		},
		{
			icon: FaYoutube,
			label: 'YouTube',
			href: 'https://www.youtube.com/channel/UCSZnPvsIW2heYmWezip3Q-g',
		},
		{
			icon: FaLinkedinIn,
			label: 'LinkedIn',
			href: 'https://www.linkedin.com/company/submittable/',
		},
	];

	const podcastLinks = [
		{
			label: 'Spotify',
			href: 'https://open.spotify.com/show/0VqgWXAGMZ4s5Rc7g5LNuc',
			icon: FaSpotify,
		},
		{
			label: 'Apple',
			href: 'https://podcasts.apple.com/us/podcast/impact-audio/id1576770635',
			icon: FaPodcast,
		},
	];

	const [hiring, setHiring] = useState(null);

	useEffect(() => {
		const checkHiringStatus = async () => {
			try {
				const response = await fetch(
					'https://boards-api.greenhouse.io/v1/boards/submittable/jobs?content=true'
				);

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();

				// Check if there are any jobs returned
				setHiring(Array.isArray(data.jobs) && data.jobs.length > 0);
			} catch (error) {
				console.error('Error fetching jobs:', error);
				setHiring(false); // fallback in case of error
			}
		};

		checkHiringStatus();
	}, []); // Run only once on component mount

	const data = useStaticQuery(graphql`
		query footerQuery {
			footerMenu: contentfulMenu(
				contentful_id: { eq: "5UJsfR0RNf3dS0GKt2KGQo" }
			) {
				menuTitle
				menuItems {
					... on ContentfulMenu {
						menuTitleToDisplay
						menuItems {
							... on ContentfulMenuItem {
								...MenuItemFragment
							}
							... on ContentfulPageTemplateLegacySolutionsPage {
								...SolutionsPageMenuFragment
							}
						}
					}
				}
			}
		}
	`);

	const footerMenu = data.footerMenu.menuItems;

	return (
		<FooterContainer ref={footerRef}>
			<FooterWrapper>
				{/* Navigation Grid */}
				{!noMenu && (
					<>
						<NavigationGrid>
							{footerMenu.map((menuItem, index) => (
								<NavigationSection key={index}>
									<SectionTitle>{menuItem.menuTitleToDisplay}</SectionTitle>
									<LinkList>
										{menuItem.menuItems.map((link, linkIndex) => (
											<LinkItem key={`${linkIndex} - ${link.linkMainText}`}>
												{link.linkMainText === 'Careers' ? (
													<BadgeContainer>
														<FooterLink to={link.linkUrl}>
															{link.linkMainText}
														</FooterLink>
														{hiring && <Badge>Hiring</Badge>}
													</BadgeContainer>
												) : (
													<FooterLink to={link.linkUrl}>
														{link.linkMainText}
													</FooterLink>
												)}
											</LinkItem>
										))}
									</LinkList>
								</NavigationSection>
							))}
						</NavigationGrid>
						<Divider />
					</>
				)}

				{/* Main Headline and Social/Podcast Section */}
				<BottomSection noMenu={noMenu}>
					{noMenu && <FooterLogoImg src={FooterLogo} />}
					{/* Main Headline */}
					<HeadlineContainer>
						<MainHeadline>
							<HeadlineBlock>Accelerating good work</HeadlineBlock>
							<HeadlineBlock>
								at{' '}
								<AccentedText>
									<span>great</span>
								</AccentedText>{' '}
								scale
							</HeadlineBlock>
						</MainHeadline>
					</HeadlineContainer>

					{/* Social Icons and Podcast */}
					<SocialContainer>
						{/* Social Icons */}
						<SocialIcons>
							{socialIcons.map((social, index) => (
								<SocialLink
									key={index}
									href={social.href}
									aria-label={social.label}
								>
									<social.icon size={19} />
								</SocialLink>
							))}
						</SocialIcons>

						{/* Podcast Section */}

						<PodcastSection>
							<PodcastLabel>Listen to our podcast Impact Audio</PodcastLabel>
							<SocialIcons>
								{podcastLinks.map((podcast, index) => (
									<SocialLink
										key={index}
										href={podcast.href}
										aria-label={podcast.label}
									>
										<podcast.icon size={19} />
									</SocialLink>
								))}
							</SocialIcons>
						</PodcastSection>
					</SocialContainer>
				</BottomSection>
			</FooterWrapper>
		</FooterContainer>
	);
};

export default Footer;
